import { Form, Formik } from "formik";
import * as Yup from "yup";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { type RadioItemProps } from "@vega-core/components/Form/Controls/Radio/RadioGroupField/RadioGroupField";
import { RadioGroupField } from "@vega-core/components/Form/Fields/RadioGroupField";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";

type Props = {
  isSubmitting: boolean;
  onSubmit: (hasAccepted: boolean) => void;
};

export const AccreditedInvestorStatusView = ({
  isSubmitting,
  onSubmit,
}: Props) => {
  const {
    global: { companyName },
  } = usePublicConfig();

  const criteria = [
    {
      title: m.welcome_accreditation_financialCriteriaTitle(),
      content: [
        m.welcome_accreditation_financialCriteriaDescription1(),
        m.welcome_accreditation_financialCriteriaDescription2(),
      ],
    },
    {
      title: m.welcome_accreditation_professionalCriteriaTitle(),
      content: [m.welcome_accreditation_professionalCriteriaDescription1()],
    },
  ];

  const options: RadioItemProps[] = [
    {
      title: m.welcome_accreditation_confirm(),
      value: "true",
    },
    {
      title: m.welcome_accreditation_reject(),
      value: "false",
    },
  ];

  const validationSchema = Yup.object({
    accreditation: Yup.string().required(m.formValidation_requiredText()),
  });

  return (
    <div className="max-w-screen-xl p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 [grid-template-areas:'intro'_'criteria'_'form'] lg:[grid-template-areas:'intro_criteria'_'form_criteria']">
      {/* Description Section */}
      <div className="content-end lg:col-span-1 [grid-area:intro] lg:[grid-area:intro_form]">
        <div className="space-y-4 text-left">
          <h4 className="heading-4">{m.welcome_accreditation_heading()}</h4>
          <p className="body-base-regular text-text/primary">
            {m.welcome_accreditation_description({ companyName })}
          </p>
          <a
            className="rich-link text-text/interactive"
            href="https://www.sec.gov/resources-small-businesses/capital-raising-building-blocks/accredited-investors"
            rel="noreferrer noopener"
            target="_blank"
          >
            {m.welcome_accreditation_learnMore()}
          </a>
        </div>
      </div>
      {/* Form Section */}
      <div className="lg:col-span-1 [grid-area:form] lg:[grid-area:intro_form]">
        <Formik
          initialValues={{ accreditation: "" }}
          onSubmit={(values) => {
            onSubmit(values.accreditation === "true");
          }}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, isValid, values }) => (
            <Form className="flex flex-col space-y-4" onSubmit={handleSubmit}>
              <div className="text-left">
                <RadioGroupField
                  items={options}
                  labelVariant="compact"
                  name="accreditation"
                  variant="vertical"
                />
              </div>
              <ThemedButton
                className="w-48 place-self-center lg:place-self-auto"
                dataTestId="continue-button"
                disabled={
                  !isValid || isSubmitting || values.accreditation === ""
                }
                fullWidth
                isLoading={isSubmitting}
                type="submit"
                variant="primary"
              >
                {m.btn_continue()}
              </ThemedButton>
            </Form>
          )}
        </Formik>
      </div>
      {/* Criteria Section */}
      <div className="lg:col-start-2 [grid-area:criteria] lg:[grid-area:criteria] row-span-2 flex flex-col gap-4">
        {criteria.map((criteriaItem) => (
          <div
            className="body-base-regular bg-background/primary text-left divide-y divide-border/primary rounded-lg border border-border/primary"
            key={criteriaItem.title}
          >
            <h2 className="text-text/primary body-large-semibold m-3">
              {criteriaItem.title}
            </h2>
            <div className="p-4">
              <ul className="list-disc pl-6 marker:text-text/secondary">
                {criteriaItem.content.map((item) => (
                  <li className="text-sm leading-relaxed pl-1.5" key={item}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
